import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Gantt from 'frappe-gantt';
import '../estilos/frappe-gantt.css';
import {
  computarMetaActividades,
  calcularAvancesAgregados,
  calcularAvanceEsperado,
  colorDesdeAvance,
  esVisible
} from '../utils/actividadesPrograma';
import { hoyEnSantiago } from '../utils/fechaPrograma';

const CLASE_POR_COLOR = {
  '#22c55e': 'verde',
  '#eab308': 'amarillo',
  '#f97316': 'naranjo',
  '#ef4444': 'rojo'
};

const VIEW_MODES = [
  { valor: 'Day', etiqueta: 'Día' },
  { valor: 'Week', etiqueta: 'Semana' },
  { valor: 'Month', etiqueta: 'Mes' }
];

// El alto de fila (bar_height + padding) y el ancho de columna se recalculan dinámicamente
// (ver altoFila/columnWidth más abajo) para que la carta Gantt siempre quepa completa en
// pantalla al expandir/colapsar, sin scroll. ALTO_HEADER debe coincidir con
// upper_header_height + lower_header_height + 10 (defaults de la librería: 45+30+10).
const ALTO_HEADER = 85;
const ALTO_FILA_MIN = 16; // piso técnico: que el texto y el chevron sigan siendo clicables
const ALTO_FILA_MAX = 56; // tamaño "normal" cuando hay pocas filas visibles
const ALTO_VENTANA_MINIMO = 320;
const MARGEN_INFERIOR = 24; // aire hasta el borde inferior de la ventana
const ANCHO_DISPONIBLE_MINIMO = 200;
const MARGEN_DERECHO = 20; // aire hasta el borde derecho del panel

// Ancho del panel de nombres: se calcula según el nombre más largo visible para que
// ninguno quede cortado, entre un mínimo razonable y un máximo para no achicar demasiado
// el área de la carta.
const ANCHO_NOMBRES_MIN = 260;
const ANCHO_NOMBRES_MAX = 520;
const FUENTE_FILA_NOMBRES = "13.44px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

function medirTextoPx(texto, fuente) {
  if (!medirTextoPx.ctx) {
    medirTextoPx.ctx = document.createElement('canvas').getContext('2d');
  }
  medirTextoPx.ctx.font = fuente;
  return medirTextoPx.ctx.measureText(texto).width;
}

// Días aproximados por columna según el modo de vista, para estimar cuántas columnas
// necesita el rango de fechas visible.
const PASO_DIAS = { Day: 1, Week: 7, Month: 30.4368 };
const MARGEN_COLUMNAS = 1.15; // holgura por los paddings internos que agrega frappe-gantt
const ANCHO_COLUMNA_MINIMO = 3;

function toFechaISO(fecha) {
  if (!fecha) return null;
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

// Avance ponderado por duración de un conjunto de actividades hoja
function avancePonderado(acts) {
  let sumDur = 0, sumAvance = 0;
  for (const a of acts) {
    const dur = a.duracionBase || 1;
    sumDur += dur;
    sumAvance += (a.porcentajeAvance || 0) * dur;
  }
  return sumDur > 0 ? Math.round(sumAvance / sumDur) : 0;
}

export default function GanttPrograma({ datos }) {
  const [seccionesColapsadas, setSeccionesColapsadas] = useState(() => new Set(datos.secciones.map(s => s._id)));
  const [actividadesColapsadas, setActividadesColapsadas] = useState(new Set());
  const [viewMode, setViewMode] = useState('Week');
  const [ganttListo, setGanttListo] = useState(false);
  const [anchoContenido, setAnchoContenido] = useState(0);
  const [altoVisible, setAltoVisible] = useState(560);
  const [anchoDisponible, setAnchoDisponible] = useState(0);

  const arbolPorSeccion = useMemo(() => {
    const map = {};
    for (const sec of datos.secciones) {
      const acts = datos.actividades
        .filter(a => a.seccion === sec._id || a.seccion?._id === sec._id)
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      map[sec._id] = { seccion: sec, ...computarMetaActividades(acts) };
    }
    return map;
  }, [datos]);

  // Todos los códigos WBS que son grupos (padres) en cualquier sección
  const todosParentCodes = useMemo(() => {
    const s = new Set();
    for (const { parentCodes } of Object.values(arbolPorSeccion)) {
      for (const code of parentCodes) s.add(code);
    }
    return s;
  }, [arbolPorSeccion]);

  // Al cargar/cambiar el programa: secciones colapsadas y, al expandir una,
  // mostrar solo sus grupos WBS de primer nivel (progresión igual que la tabla).
  useEffect(() => {
    setSeccionesColapsadas(new Set(datos.secciones.map(s => s._id)));
    setActividadesColapsadas(new Set(todosParentCodes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datos.secciones]);

  const { avanceCalc } = useMemo(
    () => calcularAvancesAgregados(datos.actividades),
    [datos.actividades]
  );

  const hoy = useMemo(() => hoyEnSantiago(), []);

  // Mismo semáforo verde/amarillo/naranjo/rojo que usa la tabla (colorDesdeAvance),
  // según avance real vs. avance esperado a la fecha, para hablar el mismo idioma visual.
  const claseSemaforo = useCallback((avance, inicio, fin, esGrupo) => {
    const esperado = calcularAvanceEsperado(inicio, fin, hoy);
    const color = colorDesdeAvance(avance, esperado);
    const token = CLASE_POR_COLOR[color] || 'amarillo';
    return `gantt-avance-${token}${esGrupo ? '-grupo' : ''}`;
  }, [hoy]);

  // Lista única de filas (secciones + grupos WBS + partidas visibles), en orden.
  // Es la única fuente tanto para el panel de nombres como para las barras de frappe-gantt,
  // así ambos quedan alineados 1:1 fila por fila.
  const filas = useMemo(() => {
    const lista = [];

    for (const { seccion, actividades: acts, parentCodes } of Object.values(arbolPorSeccion)) {
      const seccionColapsada = seccionesColapsadas.has(seccion._id);
      const hojas = acts.filter(a => !a.wbsCode || !parentCodes.has(a.wbsCode));
      const inicios = hojas.map(a => toFechaISO(a.fechaInicioBase)).filter(Boolean).sort();
      const fines = hojas.map(a => toFechaISO(a.fechaFinBase)).filter(Boolean).sort();

      if (inicios.length && fines.length) {
        const inicio = inicios[0];
        const fin = fines[fines.length - 1];
        const progreso = avancePonderado(hojas);
        lista.push({
          id: `sec-${seccion._id}`,
          nombre: seccion.nombre,
          wbsCode: seccion.wbsCode || '',
          relDepth: 0,
          esGrupo: true,
          colapsado: seccionColapsada,
          start: inicio,
          end: fin,
          progress: progreso,
          claseEstado: claseSemaforo(progreso, inicio, fin, true),
          seccionId: seccion._id,
          actId: null,
          wbsGrupo: null
        });
      }

      if (seccionColapsada) continue;

      const visibles = acts.filter(a => esVisible(a, actividadesColapsadas));
      for (const act of visibles) {
        const start = toFechaISO(act.fechaInicioBase);
        const end = toFechaISO(act.fechaFinBase);
        if (!start || !end) continue;

        const avance = act.esParent
          ? (avanceCalc[act.wbsCode] ?? act.porcentajeAvance ?? 0)
          : (act.porcentajeAvance ?? 0);

        lista.push({
          id: act._id,
          nombre: act.nombre,
          wbsCode: act.wbsCode || '',
          relDepth: act.relDepth + 1,
          esGrupo: act.esParent,
          colapsado: act.esParent && actividadesColapsadas.has(act.wbsCode),
          start,
          end,
          progress: avance,
          claseEstado: claseSemaforo(avance, start, end, act.esParent),
          seccionId: seccion._id,
          actId: act._id,
          wbsGrupo: act.wbsCode
        });
      }
    }

    return lista;
  }, [arbolPorSeccion, seccionesColapsadas, actividadesColapsadas, avanceCalc, claseSemaforo]);

  // Ancho del panel de nombres ajustado al nombre más largo actualmente visible,
  // para que ninguno se vea cortado (con un tope máximo para no achicar la carta).
  const anchoNombres = useMemo(() => {
    let maxNecesario = ANCHO_NOMBRES_MIN;
    for (const f of filas) {
      const etiqueta = `${f.wbsCode ? f.wbsCode + ' · ' : ''}${f.nombre}`;
      const anchoTexto = medirTextoPx(etiqueta, FUENTE_FILA_NOMBRES);
      const indent = 12 + f.relDepth * 18;
      const iconoYGap = 24;
      const paddingDerecho = 24;
      const necesario = indent + iconoYGap + anchoTexto + paddingDerecho;
      if (necesario > maxNecesario) maxNecesario = necesario;
    }
    return Math.min(ANCHO_NOMBRES_MAX, Math.round(maxNecesario));
  }, [filas]);

  const tareas = useMemo(() => {
    const ids = new Set(filas.map(f => f.id));
    return filas.map(f => {
      const act = f.actId ? datos.actividades.find(a => a._id === f.actId) : null;
      const deps = (act?.dependencias || [])
        .map(d => d.actividadId)
        .filter(id => ids.has(id));
      return {
        id: f.id,
        name: f.nombre || f.wbsCode || '',
        start: f.start,
        end: f.end,
        progress: f.progress,
        dependencies: deps.join(','),
        custom_class: f.claseEstado,
        _seccionId: f.seccionId,
        _actId: f.actId,
        _wbsCode: f.wbsGrupo,
        _esGrupo: f.esGrupo
      };
    });
  }, [filas, datos.actividades]);

  const alTogglear = ({ seccionId, actId, esGrupo, wbsGrupo }) => {
    if (!esGrupo) return;
    if (actId === null) {
      setSeccionesColapsadas(prev => {
        const next = new Set(prev);
        if (next.has(seccionId)) next.delete(seccionId); else next.add(seccionId);
        return next;
      });
      return;
    }
    if (wbsGrupo) {
      setActividadesColapsadas(prev => {
        const next = new Set(prev);
        if (next.has(wbsGrupo)) next.delete(wbsGrupo); else next.add(wbsGrupo);
        return next;
      });
    }
  };

  const contenedorRef = useRef(null);
  const nombresRef = useRef(null);
  const scrollXRef = useRef(null);
  const splitRef = useRef(null);
  const wrapperRef = useRef(null);
  const ganttRef = useRef(null);

  const alClickBarra = (task) => {
    alTogglear({ seccionId: task._seccionId, actId: task._actId, esGrupo: task._esGrupo, wbsGrupo: task._wbsCode });
  };

  // Mide el espacio disponible (alto hasta el borde de la ventana, ancho del panel de
  // la carta) para que las filas y columnas se puedan recalcular a su tamaño exacto.
  const medirDisponible = useCallback(() => {
    if (!splitRef.current || !wrapperRef.current) return;
    const top = splitRef.current.getBoundingClientRect().top;
    const alto = window.innerHeight - top - MARGEN_INFERIOR;
    setAltoVisible(Math.max(ALTO_VENTANA_MINIMO, Math.round(alto)));
    const ancho = wrapperRef.current.clientWidth - MARGEN_DERECHO;
    setAnchoDisponible(Math.max(ANCHO_DISPONIBLE_MINIMO, Math.round(ancho)));
  }, []);

  useEffect(() => {
    medirDisponible();
    window.addEventListener('resize', medirDisponible);
    return () => window.removeEventListener('resize', medirDisponible);
  }, [datos.secciones, medirDisponible]);

  // El ancho del panel de nombres cambia el ancho restante para la carta: re-medir.
  useEffect(() => {
    medirDisponible();
  }, [anchoNombres, medirDisponible]);

  // Alto de fila dinámico: todas las filas visibles deben caber en altoVisible sin scroll.
  const altoFila = useMemo(() => {
    if (filas.length === 0) return ALTO_FILA_MAX;
    const disponible = altoVisible - ALTO_HEADER;
    const pitch = Math.floor(disponible / filas.length);
    return Math.max(ALTO_FILA_MIN, Math.min(ALTO_FILA_MAX, pitch));
  }, [filas.length, altoVisible]);

  const { barHeight, ganttPadding } = useMemo(() => {
    const pad = Math.max(2, Math.round(altoFila * 0.3));
    return { barHeight: Math.max(8, altoFila - pad), ganttPadding: pad };
  }, [altoFila]);

  // Ancho de columna dinámico: el rango de fechas visible debe caber en anchoDisponible sin scroll.
  const columnWidth = useMemo(() => {
    if (tareas.length === 0 || anchoDisponible <= 0) return null;
    let minT = Infinity, maxT = -Infinity;
    for (const t of tareas) {
      const s = new Date(t.start).getTime();
      const e = new Date(t.end).getTime();
      if (s < minT) minT = s;
      if (e > maxT) maxT = e;
    }
    const spanDias = Math.max(1, (maxT - minT) / 86400000);
    const columnas = Math.ceil((spanDias / PASO_DIAS[viewMode]) * MARGEN_COLUMNAS) + 2;
    return Math.max(ANCHO_COLUMNA_MINIMO, Math.floor(anchoDisponible / columnas));
  }, [tareas, anchoDisponible, viewMode]);

  useEffect(() => {
    if (!contenedorRef.current || tareas.length === 0) return;

    if (!ganttRef.current) {
      ganttRef.current = new Gantt(contenedorRef.current, tareas, {
        view_mode: viewMode,
        readonly: true,
        readonly_dates: true,
        readonly_progress: true,
        scroll_to: 'today',
        container_height: altoVisible,
        bar_height: barHeight,
        padding: ganttPadding,
        ...(columnWidth ? { column_width: columnWidth } : {}),
        on_click: alClickBarra
      });
      setGanttListo(true);
    } else {
      ganttRef.current.refresh(tareas);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tareas]);

  useEffect(() => {
    if (ganttRef.current) ganttRef.current.change_view_mode(viewMode);
  }, [viewMode]);

  // Aplica el nuevo alto/ancho/tamaño de fila (por resize o por expandir/colapsar filas)
  // a una instancia de Gantt ya creada.
  useEffect(() => {
    if (!ganttRef.current) return;
    ganttRef.current.update_options({
      container_height: altoVisible,
      bar_height: barHeight,
      padding: ganttPadding,
      ...(columnWidth ? { column_width: columnWidth } : {})
    });
  }, [altoVisible, barHeight, ganttPadding, columnWidth]);

  // Vuelve a medir el ancho real del contenido (cambia con el rango de fechas y con
  // el modo Día/Semana/Mes) para dimensionar la barra de scroll horizontal de abajo.
  useEffect(() => {
    if (!ganttListo || !ganttRef.current) return;
    const id = requestAnimationFrame(() => {
      if (ganttRef.current) setAnchoContenido(ganttRef.current.$container.scrollWidth);
    });
    return () => cancelAnimationFrame(id);
  }, [tareas, viewMode, ganttListo]);

  // Sincroniza el scroll (vertical con el panel de nombres, horizontal con la barra de abajo)
  // con el contenedor interno de frappe-gantt.
  useEffect(() => {
    if (!ganttListo || !ganttRef.current || !nombresRef.current || !scrollXRef.current) return;
    const ganttEl = ganttRef.current.$container;
    const nombresEl = nombresRef.current;
    const scrollXEl = scrollXRef.current;
    let sincronizando = false;

    const alScrollGantt = () => {
      if (sincronizando) return;
      sincronizando = true;
      nombresEl.scrollTop = ganttEl.scrollTop;
      scrollXEl.scrollLeft = ganttEl.scrollLeft;
      sincronizando = false;
    };
    const alScrollNombres = () => {
      if (sincronizando) return;
      sincronizando = true;
      ganttEl.scrollTop = nombresEl.scrollTop;
      sincronizando = false;
    };
    const alScrollX = () => {
      if (sincronizando) return;
      sincronizando = true;
      ganttEl.scrollLeft = scrollXEl.scrollLeft;
      sincronizando = false;
    };

    ganttEl.addEventListener('scroll', alScrollGantt);
    nombresEl.addEventListener('scroll', alScrollNombres);
    scrollXEl.addEventListener('scroll', alScrollX);
    return () => {
      ganttEl.removeEventListener('scroll', alScrollGantt);
      nombresEl.removeEventListener('scroll', alScrollNombres);
      scrollXEl.removeEventListener('scroll', alScrollX);
    };
  }, [ganttListo]);

  if (datos.secciones.length === 0) {
    return <div className="programa-empty"><p>No hay secciones con actividades para mostrar en la carta Gantt.</p></div>;
  }

  return (
    <div className="programa-gantt">
      <div className="programa-gantt-controles">
        <div className="programa-gantt-vista-modo">
          {VIEW_MODES.map(m => (
            <button
              key={m.valor}
              className={`programa-ctrl-btn${viewMode === m.valor ? ' activo' : ''}`}
              onClick={() => setViewMode(m.valor)}
            >
              {m.etiqueta}
            </button>
          ))}
        </div>
      </div>

      {tareas.length === 0 && (
        <div className="programa-empty"><p>Las actividades visibles no tienen fechas base para graficar. Expande una sección con actividades fechadas.</p></div>
      )}

      <div className="programa-gantt-split" ref={splitRef} style={{ display: tareas.length === 0 ? 'none' : 'flex' }}>
        <div className="programa-gantt-nombres" ref={nombresRef} style={{ height: altoVisible, width: anchoNombres }}>
          <div className="programa-gantt-nombres-header" style={{ height: ALTO_HEADER }}>Actividad</div>
          {filas.map(f => {
            const etiqueta = `${f.wbsCode ? f.wbsCode + ' · ' : ''}${f.nombre}`;
            return (
              <div
                key={f.id}
                className={`programa-gantt-fila-nombre${f.esGrupo ? ' clicable' : ''}${f.actId === null ? ' es-seccion' : ''}`}
                style={{ height: altoFila, paddingLeft: 12 + f.relDepth * 18 }}
                onClick={() => alTogglear({ seccionId: f.seccionId, actId: f.actId, esGrupo: f.esGrupo, wbsGrupo: f.wbsGrupo })}
                title={f.esGrupo ? `${etiqueta} — clic para expandir/colapsar` : etiqueta}
              >
                {f.esGrupo
                  ? <span className={`programa-chevron-sm${f.colapsado ? ' colapsado' : ''}`}>▾</span>
                  : <span className="programa-leaf-dot" />
                }
                {f.wbsCode && <span className="programa-wbs">{f.wbsCode}</span>}
                <span className="programa-gantt-nombre-texto">{f.nombre}</span>
              </div>
            );
          })}
        </div>

        <div className="programa-gantt-wrapper" ref={wrapperRef}>
          <div ref={contenedorRef}></div>
          <div className="programa-gantt-scrollbar-x" ref={scrollXRef}>
            <div style={{ width: anchoContenido, height: 1 }} />
          </div>
        </div>
      </div>
    </div>
  );
}
